<script setup lang="ts">
import { computed } from 'vue';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { NightsSelector } from '@apps/main/new-desktop/modules/nightsSelector';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';

import {
  AppSvgIcon,
  AppFlicker,
  AppTrashCanButton,
  AppCustomPlaceholder,
} from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type {
  ItineraryDateLocationLoaderSegment,
  ItineraryDateLocationSegment,
} from '../../../models';
import { useTripOverviewModalsStore } from '../../../stores';
import ItineraryTimeline from '../ItineraryTimeline.vue';

const { t } = i18n.global;

interface ItineraryDateLocationSegmentProps {
  segment: ItineraryDateLocationSegment | ItineraryDateLocationLoaderSegment;
  printMode: boolean;
  isLoading?: boolean;
}
const props = defineProps<ItineraryDateLocationSegmentProps>();

interface ItineraryDateLocationSegmentEmits {
  (event: 'segment-clicked'): void;
}
const emit = defineEmits<ItineraryDateLocationSegmentEmits>();

const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const editTripStore = useEditTripStore();

const isSegmentLoaded = computed<boolean>(() => 'time' in props.segment);

const nightsText = computed(
  () =>
    `${props.segment.numberOfNights ?? 0} ${t(
      'planner.nights',
      props.segment.numberOfNights ?? 0
    ).toLocaleLowerCase()}`
);

const countryText = computed(() =>
  props.segment.location.country ? `, ${props.segment.location.country}` : ''
);

const dateText = computed(() => {
  if (!isSegmentLoaded.value) {
    return '';
  }
  const segment = props.segment as ItineraryDateLocationSegment;
  return browserStore.isSmallerThan900px
    ? segment.time.absoluteShortDays
    : segment.time.absoluteDays;
});

function onTrashCanClick(ingredientId: IngredientId): void {
  tripOverviewModalsStore.openRemoveDestinationModal(ingredientId);
}

function changeNumberOfNights(
  segment: ItineraryDateLocationSegment | ItineraryDateLocationLoaderSegment,
  newNumberOfNights: number
): void {
  editTripStore.changeNumberOfNights(segment.ingredientId, newNumberOfNights);
}

const placeholderTitle = computed<string>(
  () => `${props.segment.location.name}${countryText.value}`
);

const placeholderDescription = computed<string>(() => {
  if (!dateText.value) {
    return '';
  }
  return browserStore.isSmallerThan900px && nightsText.value
    ? `${dateText.value}, ${nightsText.value}`
    : dateText.value;
});
</script>

<template>
  <ItineraryTimeline :segment-type="props.segment.segmentType">
    <template #banner>
      <AppCustomPlaceholder
        class="destination-placeholder"
        icon-name="accommodation"
        icon-color="color-brand-summer"
        :title="placeholderTitle"
        :description="placeholderDescription"
        :is-small-device="browserStore.isSmallerThan900px"
        :is-custom="true"
        :is-loading="props.isLoading"
        @click.stop="emit('segment-clicked')"
      >
        <template #left-content>
          <div class="day-range">
            <span>{{ $t('planner.days', [0]) }}</span>
            <span v-if="isSegmentLoaded" class="days">
              {{ (segment as ItineraryDateLocationSegment).time.relativeDays }}
            </span>
            <AppFlicker v-else width="spacing-32" height="spacing-20" />
          </div>
        </template>

        <template #actions>
          <AppSvgIcon
            v-if="browserStore.isSmallerThan900px"
            name="threeDotsHorizontal"
            size="18"
          />
          <div v-else class="destination-actions-desktop">
            <AppFlicker
              v-if="props.isLoading"
              width="spacing-96"
              height="spacing-32"
              :margin="{ xSpacing: 'spacing-8', ySpacing: 'spacing-8' }"
            />
            <NightsSelector
              v-else
              class="nights-selector-desktop no-parent-hover"
              :class="{ 'print-mode': props.printMode }"
              :number-of-nights="segment.numberOfNights"
              app-select-type="transparent"
              app-select-size="size-m"
              :flicker-margin="{ xSpacing: 'spacing-8' }"
              :disabled="props.printMode"
              :use-teleport="true"
              font-variant="font-bold"
              @change-number-of-nights="changeNumberOfNights(segment, $event)"
              @open-number-of-nights="
                statsigEventsStore.logCoreNightsSelectorOpened({
                  source: CoreEventSource.TripOverview,
                })
              "
            />

            <AppFlicker
              v-if="props.isLoading"
              class="trash-icon no-parent-hover"
              width="spacing-32"
              height="spacing-32"
            />
            <AppTrashCanButton
              v-else-if="!props.printMode"
              class="trash-icon"
              :aria-label="$t('a11y.remove_destination')"
              @click.stop="onTrashCanClick(segment.ingredientId)"
            />
          </div>
        </template>
      </AppCustomPlaceholder>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.destination-placeholder {
  @include for-less-or-equal-than-1199-px {
    margin-left: var(--spacing-8);
  }
  cursor: pointer;
  @include for-less-or-equal-than-599-px {
    margin-left: 0;
  }
  .day-range {
    display: flex;
    flex-direction: column;
    align-items: center;
    @include font-body-s-light;

    .days {
      @include font-body-m-bold;
    }
  }
  .destination-actions-desktop {
    display: flex;
    align-items: center;
    .nights-selector-desktop {
      all: unset;
      height: 100%;
      display: flex;
      align-items: center;
      &:not(.print-mode) {
        border-right: 1px solid var(--color-neutral-20);
        margin-right: var(--spacing-8);
      }
    }
    .trash-icon {
      cursor: pointer;
      color: var(--color-neutral-70);
    }
  }
}
</style>
